import { JSX, useState } from "react";
import { FilterListInterface } from "../../interfaces/FilterListInterface";
import { getLocalizedText } from "../../utils/getLocalizedText";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectFilter } from "../../../pages/productRangePage/ProductRangeSlice";
import { LocalizedLabelInterface } from "../../interfaces/LocalizedLabelInterface";
import { ProductFilter } from "@hatch-digital-nl/bravilor-storybook";

interface ProductFilterWrapperProps {
    filter: FilterListInterface;
    index: number;
}

interface IndexedDBOption {
    code: string;
    id: string | number;
    label: LocalizedLabelInterface | string;
    sort: number;
}

export const ProductFilterWrapper = ({
    filter,
    index,
}: ProductFilterWrapperProps): JSX.Element => {
    const dispatch = useAppDispatch();
    const { selectedFilters } = useAppSelector((state) => state.productRange);
    const [active, setActive] = useState(index < 5);

    const selectedOptionsForThisFilter =
        selectedFilters[filter.id]?.options || [];

    const transformedFilter = {
        id: filter.id,
        code: filter.code,
        label: getLocalizedText(filter.label as LocalizedLabelInterface),
        filterTooltip: getLocalizedText(
            (filter.filterTooltip ?? "") as LocalizedLabelInterface,
        ),
        options: Object.values(filter.options)
            .sort((a: IndexedDBOption, b: IndexedDBOption) => a.sort - b.sort)
            .map((option: IndexedDBOption) => ({
                id: option.id.toString(),
                label: getLocalizedText(option.label),
                value: getLocalizedText(option.label),
            })),
    };

    // Products expose the resolved attribute label as their filterAttributes
    // value (not the option code), so matching happens on the resolved label.
    const selectedOptionIds = selectedOptionsForThisFilter
        .map((selectedValue) => {
            const optionsArray: IndexedDBOption[] = Object.values(
                filter.options,
            );
            const matchingOption = optionsArray.find(
                (opt: IndexedDBOption) =>
                    getLocalizedText(opt.label) === selectedValue.value,
            );
            return matchingOption ? matchingOption.id.toString() : "";
        })
        .filter((id) => id !== "");

    const handleOptionChange = (optionId: string): void => {
        const optionsArray: IndexedDBOption[] = Object.values(filter.options);
        const option: IndexedDBOption | undefined = optionsArray.find(
            (opt: IndexedDBOption) => opt.id.toString() === optionId,
        );

        if (option) {
            dispatch(
                selectFilter({
                    filter,
                    option: {
                        id: optionId,
                        value: getLocalizedText(option.label),
                    },
                }),
            );
        }
    };

    return (
        <ProductFilter
            filter={transformedFilter}
            isMobile={true}
            isActive={active}
            selectedOptions={selectedOptionIds}
            onOptionChange={handleOptionChange}
            onToggle={() => setActive(!active)}
        />
    );
};
